import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BORNES_RECHERCHE } from '@nextilt/commun';
import { api } from '../partage/api/client';
import { cles, relireApres } from '../partage/relectures';
import type { Boucle, Bulle, Etat, Maillon } from '../partage/api/types';
import {
  ceQueProduitLeLien,
  ceQuElleAttendSelon,
  comptesDesConditions,
  estUnCycle,
  GestesDuLien,
  RefusDeCycle,
  useSortieDeBoucle,
} from '../moteur/conditions';
import { useAuPouce } from '../partage/surface';
import {
  CE_QUE_CE_LIEN_VA_PRODUIRE,
  ELLE_ATTEND,
  ELLE_EST_DEDANS,
  ELLE_RETIENT,
  L_ATTEND,
  LES_DEUX_AVANCENT_ENSEMBLE,
  LIEN_CACHE,
  LIEN_POSE,
  MOTS_ETAT,
  lienDejaFait,
  lienGardeCommeIdee,
  lienJoursDuMonde,
  lienTantQue,
  motsDuDossier,
  ouvrirLaFicheDe,
  parLaBulle,
} from '../partage/vocabulaire';
import './Dependances.css';

/**
 * Toutes les fiches se relisent (une issue de boucle retire un lien posé depuis une AUTRE bulle),
 * et ce qui montre la bulle bloquée ou libérée : « Projets », « Maintenant », la journée.
 */
function useRafraichir() {
  const client = useQueryClient();
  return () => void relireApres(client, 'poserOuRetirerUneCondition');
}

/**
 * Les liens d'une bulle et le geste qui en retire un : le bloc « Conditions » de l'ordinateur et
 * le panneau Réglages du téléphone (chantier `telephone-projets-fiche`, ticket 04) les lisent et
 * les retirent de la même façon, sous la même clé.
 */
export function useConditionsDeLaBulle(bulleId: string) {
  const rafraichir = useRafraichir();
  const liens = useQuery({ queryKey: cles.liens(bulleId), queryFn: () => api.liens(bulleId) });
  const delier = useMutation({ mutationFn: api.delier, onSuccess: rafraichir });
  return {
    attend: liens.data?.attend ?? [],
    retient: liens.data?.bloque ?? [],
    /** Ses liens d'une autre sorte, pour le bloc « Liens » de la fiche Plan (ticket 05). */
    autres: liens.data?.autresLiens ?? [],
    delier: (lienId: string) => delier.mutate(lienId),
  };
}

/**
 * Les conditions d'une bulle telles que le bloc « Conditions » les montre, et leur compte : le
 * bloc les liste, la fiche Note les compte dans sa ligne repliée (chantier types-de-fiche,
 * modification 03). La bulle qui la contient se lit dans « Elle attend » et compte dans la ligne
 * repliée (« Les papiers, elle est dedans » de la maquette), mais seule elle n'est pas une
 * condition posée : toute bulle est dans une autre, et la maquette de la bulle vide n'a pas de
 * « Conditions ».
 */
export function useConditionsPosees(bulle: Pick<Bulle, 'id' | 'etat' | 'retenuePar'>, parent: Maillon | null) {
  const { attend, retient, delier } = useConditionsDeLaBulle(bulle.id);
  const heritees = ceQuElleAttendSelon(bulle, 'conditions');
  const { posees, combienAttend } = comptesDesConditions({
    dansUneBulle: parent !== null,
    attend: attend.length,
    heritees: heritees.length,
    retient: retient.length,
  });
  return { attend, retient, delier, heritees, posees, combienAttend };
}

/**
 * Le bloc « Conditions » de la fiche Plan, déplié (chantier liens-entre-bulles, ticket 05,
 * décision 13 du cadrage : seule la fiche Plan montre les liens ; avant, une ligne repliée sur la
 * face Classique, chantier `revue-design`, ticket 02). Deux listes :
 * - « Elle attend » : la bulle qui la contient (« elle est dedans »), les liens posés à la main
 *   (« lien posé », qu'on retire d'ici), les attentes héritées d'une bulle qui la contient
 *   (« par « … » », qu'on retire depuis la fiche de cette bulle-là) ;
 * - « Elle retient » : ce qui l'attend par un lien (« l'attend »).
 *
 * Chaque lien posé a les signes de la carte (`GestesDuLien`) : l'œil, et ⇄ ; « Retirer » reste
 * pour ce qu'elle attend, la croix pour ce qu'elle retient. Un lien caché sur la carte se dit
 * « caché ». Un clic sur l'autre bulle ouvre sa fiche (`onAutreBulle`).
 *
 * Sans aucune condition posée, rien ; on en pose une par « + Condition » (`PoserUneCondition`).
 * Jamais de flèche tracée : on choisit dans une liste.
 */
export function Dependances({
  bulle,
  parent,
  onAutreBulle,
}: {
  /**
   * Ce qui la retient, calculé par le serveur (`retenuePar`). On n'en lit ici que les attentes
   * héritées : une bulle qui la contient attend quelque chose, donc elle aussi (ticket 32).
   */
  bulle: Pick<Bulle, 'id' | 'etat' | 'retenuePar'>;
  /** La condition verticale, celle qui est déduite du seul fait d'être dedans. */
  parent: Maillon | null;
  /** Ouvrir la fiche de l'autre bulle d'un lien ; sans lui, son nom se lit sans se cliquer. */
  onAutreBulle?: (id: string) => void;
}) {
  const { attend, retient, delier, heritees, posees, combienAttend } = useConditionsPosees(bulle, parent);
  if (posees === 0) return null;

  return (
    <div className="dependances">
      {combienAttend > 0 && <p className="dependances-intertitre">{ELLE_ATTEND}</p>}
      {parent && (
        <div className="dependance">
          <FlecheDeduite />
          <span className="dependance-titre">{parent.titre}</span>
          <span className="dependance-mention">{ELLE_EST_DEDANS}</span>
        </div>
      )}

      {attend.map((lien) => (
        <div key={lien.lienId} className={`dependance${lien.visible ? '' : ' dependance--cachee'}`} data-lien={lien.lienId}>
          <FlecheTransversale />
          <TitreDuLien titre={lien.titre} id={lien.id} onAutreBulle={onAutreBulle} />
          <span className="dependance-mention dependance-mention--posee">{LIEN_POSE}</span>
          {!lien.visible && <span className="dependance-mention dependance-cache">{LIEN_CACHE}</span>}
          <span className="dependance-gestes">
            <GestesDuLien lien={{ lienId: lien.lienId, sorte: 'attend', visible: lien.visible }} sansCroix echapAnnule />
          </span>
          <button
            type="button"
            className="dependance-retirer"
            onClick={() => delier(lien.lienId)}
            aria-label={`retirer la condition ${lien.titre}`}
          >
            Retirer
          </button>
        </div>
      ))}

      {/* L'attente d'une bulle qui la contient : même flèche, même ambre, mais le lien vit
          sur cette bulle-là, et c'est de sa fiche qu'on le retire. */}
      {heritees.map((r) => (
        <div key={`herite-${r.id}`} className="dependance">
          <FlecheTransversale />
          <span className="dependance-titre">{r.titre}</span>
          <span
            className="dependance-mention dependance-mention--posee"
            title={`« ${r.via.titre} » attend « ${r.titre} » : ce qu’elle contient attend aussi.`}
          >
            {parLaBulle(r.via.titre)}
          </span>
        </div>
      ))}

      {retient.length > 0 && <p className="dependances-intertitre">{ELLE_RETIENT}</p>}
      {retient.map((lien) => (
        <div
          key={`retient-${lien.lienId}`}
          className={`dependance${lien.visible ? '' : ' dependance--cachee'}`}
          data-lien={lien.lienId}
        >
          <FlecheRetenue />
          <TitreDuLien titre={lien.titre} id={lien.id} onAutreBulle={onAutreBulle} />
          <span className="dependance-mention">{L_ATTEND}</span>
          {!lien.visible && <span className="dependance-mention dependance-cache">{LIEN_CACHE}</span>}
          <span className="dependance-gestes">
            <GestesDuLien lien={{ lienId: lien.lienId, sorte: 'attend', visible: lien.visible }} echapAnnule />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Le nom de l'autre bulle d'un lien : un clic ouvre sa fiche, quand la fiche sait l'ouvrir. */
export function TitreDuLien({ titre, id, onAutreBulle }: { titre: string; id: string; onAutreBulle?: (id: string) => void }) {
  if (!onAutreBulle) return <span className="dependance-titre">{titre}</span>;
  return (
    <button type="button" className="dependance-titre dependance-titre--bouton" title={ouvrirLaFicheDe(titre)} onClick={() => onAutreBulle(id)}>
      {titre}
    </button>
  );
}

/**
 * « + Condition » : poser un lien depuis la bulle **bloquée**, c'est le sens de la §3. On
 * choisit dans une liste, jamais en traçant une flèche : le même geste au doigt et à la
 * souris (principe 8). La recherche traverse tous les dossiers : une bulle du même arbre n'a
 * pas besoin d'être liée, il suffisait de la mettre dedans.
 */
export function PoserUneCondition({
  bulleId,
  titre,
  delaiJours,
  ouvert,
  onFermer,
}: {
  bulleId: string;
  /** Le titre de la bulle bloquée : c'est elle qui pose la question « elle attend quoi ? ». */
  titre: string;
  /** Le temps que met le monde après ton geste. Il ne commence à courir qu'une fois libérée. */
  delaiJours: number | null;
  ouvert: boolean;
  onFermer: () => void;
}) {
  const rafraichir = useRafraichir();
  const liens = useQuery({ queryKey: cles.liens(bulleId), queryFn: () => api.liens(bulleId) });

  const lier = useMutation({
    mutationFn: (sourceId: string) => api.lier(sourceId, bulleId),
    onSuccess: () => {
      onFermer();
      refus.oublier();
      rafraichir();
    },
    onError: (erreur) => {
      if (estUnCycle(erreur)) {
        refus.montrer(erreur.details as Boucle);
        onFermer();
      }
    },
  });

  // Les issues de la boucle, puis le lien tenté reposé : écrites une fois (`useSortieDeBoucle`).
  const refus = useSortieDeBoucle((tente) => lier.mutate(tente.sourceId));
  const { boucle, sortir } = refus;

  function fermerLeRefus() {
    refus.fermer();
    lier.reset();
  }

  return (
    <>
      {ouvert && (
        <Chercher
          titre={titre}
          delaiJours={delaiJours}
          exclu={bulleId}
          dejaLies={(liens.data?.attend ?? []).map((l) => l.id)}
          onChoisir={(id) => lier.mutate(id)}
          onRenoncer={onFermer}
        />
      )}

      {boucle && (
        <RefusDeCycle
          boucle={boucle}
          geste="lien"
          enCours={sortir.isPending || lier.isPending}
          echec={sortir.isError || (lier.isError && !estUnCycle(lier.error))}
          onFermer={fermerLeRefus}
          onRetirer={(lienId) => sortir.mutate({ lienId })}
          onInserer={(lienId, titre) => sortir.mutate({ lienId, titre })}
        />
      )}
    </>
  );
}

/** Ce qui l'attend : la flèche part d'elle vers la bulle retenue, en trait plein. */
function FlecheRetenue() {
  return (
    <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
      <path d="M2.6 8.5h11M9.6 4.5l4 4-4 4" stroke="var(--encre-tierce)" strokeWidth="1.4" />
    </svg>
  );
}

/**
 * Une condition **verticale** : elle monte, elle est déduite du seul fait d'être dedans,
 * elle est gratuite. Trait plein, bleu-vert — c'est le chemin creusé.
 */
function FlecheDeduite() {
  return (
    <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
      <path d="M8.5 13.5V3.5M4.6 7.4L8.5 3.5l3.9 3.9" stroke="var(--accent)" strokeWidth="1.4" />
    </svg>
  );
}

/**
 * Une condition **transversale** : elle traverse, tu l'as posée à la main.
 *
 * Elle est ambre ici, et violette sur la carte. Ce n'est pas une incohérence : sur la carte
 * le lien est un trait entre deux bulles, et il doit se distinguer des liens verticaux —
 * d'où le violet. Dans la fiche, il n'y a plus de trait à départager : ce qui reste à dire,
 * c'est que cette bulle **attend**, et l'attente est ambre partout dans l'app.
 */
function FlecheTransversale() {
  return (
    <svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true">
      <path
        d="M2.6 8.5h11M9.6 4.5l4 4-4 4"
        stroke="var(--monde)"
        strokeWidth="1.4"
        strokeDasharray="2.4 2.6"
      />
    </svg>
  );
}

function Loupe() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.8 10.8l3 3" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

/** La recherche traverse tous les dossiers, et montre le chemin pour départager les homonymes. */
function Chercher({
  titre,
  delaiJours,
  exclu,
  dejaLies,
  onChoisir,
  onRenoncer,
}: {
  titre: string;
  delaiJours: number | null;
  exclu: string;
  dejaLies: string[];
  onChoisir: (id: string) => void;
  onRenoncer: () => void;
}) {
  const [texte, setTexte] = useState('');
  const m = motsDuDossier(useAuPouce());
  const recherche = useQuery({
    queryKey: cles.recherche(texte),
    queryFn: () => api.rechercher(texte),
    enabled: texte.trim().length >= BORNES_RECHERCHE.signesMin,
  });

  const ecartes = new Set([exclu, ...dejaLies]);
  const resultats = (recherche.data?.resultats ?? []).filter((r) => !ecartes.has(r.id));
  const candidat = resultats[0] ?? null;

  return (
    <>
      <div className="chercher">
        <div className="chercher-tete">
          <label htmlFor="chercher-lien">
            « {titre} » <b>attend</b> quoi&nbsp;?
          </label>
          {/* Une touche nue, pas une touche encadrée : c'est un rappel, pas un bouton. */}
          <button type="button" className="chercher-echap" onClick={onRenoncer}>
            Échap
          </button>
        </div>

        <div className="chercher-champ">
          <Loupe />
          <input
            id="chercher-lien"
            data-tuto="champ-lier"
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder="Photo"
            autoFocus
            onKeyDown={(e) => {
              // Échap ne range que la recherche : sans ceci, il remonte jusqu'à la fenêtre, où
              // la fiche, déjà redessinée sans elle, le prend pour elle et se ferme.
              if (e.key === 'Escape') {
                e.stopPropagation();
                onRenoncer();
              }
            }}
          />
          <span className="chercher-portee">{m.tousLes}</span>
        </div>

        {texte.trim().length >= BORNES_RECHERCHE.signesMin && (
          <ul className="chercher-resultats">
            {resultats.length === 0 && <li className="chercher-rien">Rien de ce nom</li>}
            {resultats.map((r, i) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={`chercher-resultat${i === 0 ? ' est-premier' : ''}`}
                  data-tuto="resultat-lier"
                  onClick={() => onChoisir(r.id)}
                >
                  {/*
                   * La puce devrait porter la couleur du dossier du résultat ; l'API de
                   * recherche ne la renvoie pas encore, donc elle reste neutre plutôt que
                   * d'affirmer une couleur fausse.
                   */}
                  <span className="chercher-puce" aria-hidden="true" />
                  <span className="chercher-corps">
                    <span className="chercher-titre">{r.titre}</span>
                    <span className="chercher-chemin">
                      {(r.chemin.length > 0 ? r.chemin.join(' › ') : m.unDossier) +
                        ' · ' +
                        MOTS_ETAT[r.etat]}
                    </span>
                  </span>
                  {i === 0 && (
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                      <path
                        d="M3 7.9l3.1 3.1L12.3 4.6"
                        stroke="var(--accent)"
                        strokeWidth="1.8"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <p className="chercher-note">
          La recherche traverse <b>{m.tousLesEnMinuscules}</b>&nbsp;: c’est précisément à ça que sert
          une transversale. Une bulle du même arbre n’a pas besoin d’être liée — il suffisait de
          la mettre dedans.
        </p>
      </div>

      {/*
       * Ce qu'on comprend AVANT de poser le geste. Sans ça, lier reste une opération de
       * base de données ; avec, c'est une phrase sur ce qui va se passer.
       */}
      {candidat && (
        <div className="dependances-produire">
          <p className="dependances-produire-mot">{CE_QUE_CE_LIEN_VA_PRODUIRE}</p>
          <p className="dependances-produire-corps">
            <CorpsDuLien
              attendue={candidat.titre}
              etat={candidat.etat}
              bloquee={titre}
              delaiJours={delaiJours}
            />
          </p>
        </div>
      )}
    </>
  );
}

/**
 * La phrase de « Ce que ce lien va produire » selon l'état de la bulle attendue (ticket 02 du
 * chantier `bugs`). Acquise ou en réflexion, elle ne retient rien aujourd'hui : ni « les deux
 * avancent ensemble », ni les jours du monde, qui ne se disent que si le lien retient.
 */
function CorpsDuLien({
  attendue,
  etat,
  bloquee,
  delaiJours,
}: {
  attendue: string;
  etat: Etat;
  bloquee: string;
  delaiJours: number | null;
}) {
  switch (ceQueProduitLeLien(etat)) {
    case 'deja_faite':
      return <>{lienDejaFait(attendue)}</>;
    case 'gardee_comme_idee':
      return <>{lienGardeCommeIdee(attendue)}</>;
    default:
      return (
        <>
          {lienTantQue(attendue, bloquee)}
          <b>{LES_DEUX_AVANCENT_ENSEMBLE}</b>.
          {delaiJours != null && lienJoursDuMonde(delaiJours)}
        </>
      );
  }
}
